import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { debounceTime, filter, map, takeUntil } from 'rxjs/operators';
import { USER } from 'src/app/core/user.service';
import { ConfigurationClassDto } from 'src/app/shared/flight-log-api/maintenance/maintenance-client.model';
import { FlightType } from 'src/app/shared/flight-log-api/shared/shared-client.model';
import { getValueChanges } from 'src/util/angular/ng-forms-helpers';
import { getValidationErrorsChangesDeep } from 'src/util/angular/ng-forms-validation';
import { round } from 'src/util/math';
import { UserViewModel } from 'src/view-models/user-view-model';

@Component({
  selector: 'fl-maintenance-summary-simple',
  templateUrl: './maintenance-summary-simple.component.html',
  styleUrls: ['./maintenance-summary-simple.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MaintenanceSummaryOldComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  public maintenanceSummary: FormGroup;
  @Input()
  public isTraining = false;
  @Input()
  public configurationClass: ConfigurationClassDto | undefined;
  @Input()
  public flightType?: FlightType;
  @Input()
  public flightLogId: string;

  public startingHobbs$: Observable<number>;
  public endingHobbs$: Observable<number>;
  public validationErrors$: Observable<string[]>;
  public engineCount$ = new BehaviorSubject<number>(0);

  public ngOnChanges$ = new Subject<SimpleChanges>();

  get isFlightTypePart133() {
    return this.flightType === FlightType.Part133;
  }

  get showEngineOne(): boolean {
    const show = this.configurationClass != null && this.configurationClass.engineCount >= 1;
    return show;
  }

  get showEngineTwo(): boolean {
    return this.configurationClass != null && this.configurationClass.engineCount >= 2;
  }

  get totalTime() {
    return this.maintenanceSummary.get('totalFlightTime') as FormControl;
  }

  get features() {
    return this._user.flightLogFeatures;
  }

  constructor(@Inject(USER) private _user: UserViewModel) {}

  public ngOnChanges(changes: SimpleChanges): void {
    this.ngOnChanges$.next(changes);

    if (this.configurationClass != null) {
      // tslint:disable-next-line: no-non-null-assertion
      this.engineCount$.next(this.configurationClass!.engineCount);
    }

    // Define Observables
    // tslint:disable-next-line: no-non-null-assertion
    this.startingHobbs$ = getValueChanges(this.maintenanceSummary.get('startingHobbsReading')!);

    // tslint:disable-next-line: no-non-null-assertion
    this.endingHobbs$ = getValueChanges(this.maintenanceSummary.get('endingHobbsReading')!);

    this.validationErrors$ = getValidationErrorsChangesDeep(this.maintenanceSummary);
    combineLatest([this.startingHobbs$, this.endingHobbs$])
      .pipe(
        debounceTime(250),
        /* flights imported from the CAD will have the total flight time set
         but not the hobbs, so we want to show this until they
         put int the ending hobbs to calculate the true final value.
      */
        filter((hobbs) => hobbs[1] > 0),
        map((hobbs) => hobbs[1] - hobbs[0]),
        map((totalTime) => round(totalTime, 1)),
        takeUntil(this.ngOnChanges$),
      )
      .subscribe((totalTime) => {
        this.totalTime.setValue(totalTime);
      });

    this.totalTime.valueChanges.pipe(takeUntil(this.ngOnChanges$)).subscribe((totalTime) => {
      ['engine1', 'engine2'].forEach((engine) => {
        const engineCtrl = this.maintenanceSummary.get(engine);
        if (engineCtrl != null) {
          const engineTime = engineCtrl.get('time');
          if (engineTime != null) {
            engineTime.setValue(totalTime);
          }
        }
      });
    });
  }

  public ngOnInit() {}

  public ngOnDestroy() {
    this.ngOnChanges$.complete();
  }
}
